import md5 from 'md5'
import { useEffect, useRef, useState } from 'react'
import { BsCheckCircleFill } from 'react-icons/bs'
import { FiChevronDown, FiChevronLeft, FiX } from 'react-icons/fi'
import { toast } from 'sonner'

import { login, sendCode } from '@/api/login'
import useDeviceType from '@/hooks/useDeviceType'
import { useChatStore, useUserStore } from '@/store'

const APP_SECRET = 'Ku^jg9%am@jIzh8'
const APP_VERSION = '1.0.0'
const USER_TERMS_OF_SERVICE = 'https://m.miaobi.cn/newBrushUserServiceAgreement'
const USER_PRIVACY_POLICY = 'https://m.miaobi.cn/newBrushPrivacyAgreement'

const getDeviceId = () => {
  let deviceId = localStorage.getItem('deviceId')
  if (!deviceId) {
    deviceId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    localStorage.setItem('deviceId', deviceId)
  }
  return deviceId
}

export default function LoginModal() {
  const deviceType = useDeviceType()
  const setUser = useUserStore((state) => state.setUser)
  const { isLoginModalOpen, setIsLoginModalOpen } = useChatStore()

  const [loginStep, setLoginStep] = useState(1) // 1: Phone, 2: Verification Code
  const [phone, setPhone] = useState('')
  const [isAgreed, setIsAgreed] = useState(false)
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', ''])
  const codeInputsRef = useRef<(HTMLInputElement | null)[]>([])

  // Verification code states: 'initial', 'verifying', 'error', 'expired'
  const [verificationState, setVerificationState] = useState('initial')
  const [errorMessage, setErrorMessage] = useState('')
  const [countdown, setCountdown] = useState(26)

  // Handle countdown simulation
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>
    if (loginStep === 2 && countdown > 0 && verificationState !== 'verifying') {
      timer = setInterval(() => {
        setCountdown((c) => c - 1)
      }, 1000)
    } else if (countdown === 0 && verificationState !== 'error') {
      setTimeout(() => setVerificationState('expired'), 100)
    }
    return () => clearInterval(timer)
  }, [loginStep, countdown, verificationState])

  const handlePhoneSubmit = async () => {
    if (phone.length !== 11) {
      toast.warning('请输入正确的手机号')
      return
    }

    if (!isAgreed) {
      toast.warning('请先勾选并同意隐私政策')
      return
    }

    try {
      const timestamp = new Date().getTime()
      const deviceId = getDeviceId()
      const sign = md5(`${deviceId}${APP_VERSION}${deviceType}${phone}${timestamp}${APP_SECRET}`)

      await sendCode({
        phone,
        timestamp,
        sign
      })

      setLoginStep(2)
      setCountdown(60)
      setVerificationState('initial')
      setErrorMessage('')
      setVerificationCode(['', '', '', '', '', ''])
      toast.success('验证码已发送')
      setTimeout(() => codeInputsRef.current[0]?.focus(), 100)
    } catch (error) {
      console.error('Failed to send code:', error)
    }
  }

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return

    const newCode = [...verificationCode]
    if (value.length > 1) {
      const pastedCode = value.slice(0, 6).split('')
      for (let i = 0; i < pastedCode.length; i++) {
        newCode[i] = pastedCode[i]
      }
      setVerificationCode(newCode)

      const nextIndex = Math.min(pastedCode.length, 5)
      codeInputsRef.current[nextIndex]?.focus()

      if (pastedCode.length === 6) {
        verifyCode(newCode.join(''))
      }
      return
    }

    newCode[index] = value.slice(-1)
    setVerificationCode(newCode)

    if (value && index < 5) {
      codeInputsRef.current[index + 1]?.focus()
    }

    if ((index === 5 && value) || newCode.every((c) => c !== '')) {
      verifyCode(newCode.join(''))
    }
  }

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputsRef.current[index - 1]?.focus()
    }
  }

  const verifyCode = async (code: string) => {
    setVerificationState('verifying')

    try {
      const timestamp = new Date().getTime()
      const deviceId = getDeviceId()
      const sign = md5(`${deviceId}${APP_VERSION}${deviceType}${code}${phone}${timestamp}${APP_SECRET}`)

      const res = await login({
        phone,
        code,
        timestamp,
        sign,
        deeplink: {}
      })

      if (res.code !== 200) {
        setVerificationCode(['', '', '', '', '', ''])
        setVerificationState('error')
        setErrorMessage(res.message || '验证码错误')
        setTimeout(() => codeInputsRef.current[0]?.focus(), 100)
        return
      }

      setVerificationState('verifying')
      if (res.data?.token) {
        localStorage.setItem('token', res.data.token)
      }
      if (res.data) {
        setUser(res.data)
      }

      setTimeout(() => handleClose(), 500)
    } catch (error) {
      console.error('Login failed:', error)
      setVerificationState('error')
    }
  }

  const handleResend = async () => {
    try {
      const timestamp = new Date().getTime()
      const deviceId = getDeviceId()
      const sign = md5(`${deviceId}${APP_VERSION}${deviceType}${phone}${timestamp}${APP_SECRET}`)

      await sendCode({
        phone,
        timestamp,
        sign
      })

      setCountdown(60)
      setVerificationState('initial')
      setErrorMessage('')
      setVerificationCode(['', '', '', '', '', ''])
      codeInputsRef.current[0]?.focus()
    } catch (error) {
      console.error('Failed to resend code:', error)
    }
  }

  const handleClose = () => {
    setIsLoginModalOpen(false)
    setTimeout(() => {
      setLoginStep(1)
      setPhone('')
      setIsAgreed(false)
      setVerificationState('initial')
      setErrorMessage('')
    }, 300)
  }

  if (!isLoginModalOpen) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm transition-opacity duration-300">
      <div className="relative w-full max-w-[400px] overflow-hidden rounded-3xl bg-white p-8 shadow-2xl transition-all duration-300 animate-in fade-in zoom-in-95">
        <button onClick={handleClose} className="absolute right-4 top-4 rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
          <FiX className="h-5 w-5" />
        </button>

        {loginStep === 1 ? (
          <div className="flex flex-col items-center">
            <h2 className="mb-8 mt-2 text-xl font-bold text-gray-900">登录后免费使用完整功能</h2>

            <div className="w-full">
              <div className="flex h-12 w-full items-center overflow-hidden rounded-xl bg-gray-50 px-4 transition-colors focus-within:bg-white focus-within:ring-1 focus-within:ring-blue-500">
                <button className="flex items-center gap-1 text-gray-600 hover:text-gray-900">
                  <span>+86</span>
                  <FiChevronDown className="h-4 w-4" />
                </button>
                <div className="mx-3 h-4 w-[1px] bg-gray-300"></div>
                <input
                  type="tel"
                  maxLength={11}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                  placeholder="请输入手机号"
                  className="h-full flex-1 bg-transparent text-gray-900 placeholder:text-gray-400 focus:outline-none font-medium"
                />
              </div>

              <button
                onClick={handlePhoneSubmit}
                aria-disabled={phone.length !== 11 || !isAgreed}
                className={`mt-6 h-12 w-full rounded-xl text-base font-medium text-white transition-all ${
                  phone.length === 11 && isAgreed ? 'bg-blue-600 hover:bg-blue-700 active:scale-[0.98]' : 'bg-blue-300 cursor-not-allowed'
                }`}
              >
                下一步
              </button>

              <div className="mt-6 flex items-center justify-center gap-2">
                <button onClick={() => setIsAgreed(!isAgreed)} className="flex h-5 w-5 items-center justify-center">
                  {isAgreed ? <BsCheckCircleFill className="h-4 w-4 text-blue-600" /> : <div className="h-4 w-4 rounded-full border border-gray-300"></div>}
                </button>
                <span className="text-xs text-gray-500">
                  已阅读并同意{' '}
                  <a href={USER_TERMS_OF_SERVICE} target="_blank" rel="noopener noreferrer" className="text-gray-900 hover:underline">
                    用户协议
                  </a>
                  、
                  <a href={USER_PRIVACY_POLICY} target="_blank" rel="noopener noreferrer" className="text-gray-900 hover:underline">
                    隐私政策
                  </a>
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <button onClick={() => setLoginStep(1)} className="absolute left-4 top-4 flex items-center gap-1 rounded-full p-2 text-gray-500 hover:bg-gray-100 transition-colors">
              <FiChevronLeft className="h-5 w-5" />
              <span className="text-sm font-medium">输入 6位验证码</span>
            </button>

            <h2 className="mb-2 mt-12 text-2xl font-bold text-gray-900">输入 6 位验证码</h2>
            <p className="mb-8 text-sm text-gray-500">验证码已发送至 +86 {phone}</p>

            <div className="flex w-full justify-center gap-2 mb-6">
              {verificationCode.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    if (el) {
                      codeInputsRef.current[index] = el
                    }
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                  className={`h-14 w-12 rounded-xl border text-center text-xl font-medium transition-all focus:outline-none ${
                    verificationState === 'error'
                      ? 'border-red-400 bg-white text-red-500'
                      : digit || verificationState === 'verifying'
                        ? 'border-blue-500 bg-white text-blue-600'
                        : 'border-transparent bg-gray-50 text-gray-900 focus:border-blue-500 focus:bg-white'
                  }`}
                  disabled={verificationState === 'verifying'}
                />
              ))}
            </div>

            <div className="h-6 flex items-center justify-center">
              {verificationState === 'verifying' ? (
                <span className="text-sm text-blue-600 font-medium">验证中...</span>
              ) : verificationState === 'error' ? (
                <button onClick={handleResend} className="text-sm text-red-500 hover:text-red-600 font-medium transition-colors">
                  {errorMessage || '验证码错误'}
                </button>
              ) : countdown > 0 ? (
                <span className="text-sm text-gray-400">重新发送 {countdown}秒</span>
              ) : (
                <button onClick={handleResend} className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors">
                  重新发送
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
